import React, { useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import PropTypes from "prop-types";

// helpers:
import { convertPxToRem } from "frontend/components/helpers";
import {
    getFormattedTeams,
    getOwnerId,
    validatePrivacy,
    validateOwnership,
    validateNameOfKit
} from "./helpers";

// components:
import Button from "core/Button";
import BodyText from "core/BodyText";
import FormInput from "core/FormInput";
import FormSelect from "core/FormSelect";
import FormTextArea from "core/FormTextArea";

// styles:
import colors from "core/colors";
import { Form, CancelButton, GeneralFormError } from "./styles";

// mutations/queries:
import { ADD_NEW_KIT } from "./mutations";
import { GET_KITS_DATA } from "./queries";

const CreateKit = ({
    initialForm,
    ownedTeams,
    iconId,
    handleShowChooseKit,
    userId,
    allKits
}) => {
    // constants:
    const [form, setForm] = useState(initialForm);
    const [formError, setFormError] = useState(null);
    const [isProcessingRequest, setProcessingRequest] = useState(false);

    const formattedOwnedTeams = getFormattedTeams({ ownedTeams });
    const hasOwnerSelectMenu = ownedTeams && ownedTeams.length > 1; // individual user is a team so we make sure if there is more than 1, show select; else default to user as owner

    const formFieldLabelPrivacy = "Select Privacy (required)";
    const formFieldLabelOwner = "Select Owner (required)";

    // functions:
    const handleFormChange = (value, fieldName) => {
        setForm({ ...form, [fieldName]: value });
        setFormError(null);
    };

    const onCompleted = ({ createKit }) => {
        if (createKit.ok) {
            setTimeout(() => handleShowChooseKit(), 1000); //setTimeout to show button processing state
        } else if (createKit.errors) {
            setProcessingRequest(false);
            setFormError(createKit.errors);
        }
    };

    const onError = () => {
        setProcessingRequest(false);
        setFormError("This kit was not created. Please try again.");
    };

    const [createKit] = useMutation(ADD_NEW_KIT, {
        refetchQueries: [
            {
                query: GET_KITS_DATA,
                variables: {
                    iconId
                }
            }
        ],
        onCompleted,
        onError
    });

    const handleFormSubmit = async e => {
        e.preventDefault();
        setProcessingRequest(true);

        const ownerIdToSend = hasOwnerSelectMenu
            ? getOwnerId({
                  username: form.owner,
                  ownedTeams,
                  userId
              })
            : userId;

        if (!form.name_of_kit) {
            e.preventDefault();
            setProcessingRequest(false);
            return setFormError(`Please fill out the "Name of Kit" field.`);
        }

        if (form.privacy === formFieldLabelPrivacy) {
            e.preventDefault();
            setProcessingRequest(false);
            return setFormError(`Please select private or public`);
        }

        if (hasOwnerSelectMenu && form.owner === formFieldLabelOwner) {
            e.preventDefault();
            setProcessingRequest(false);
            return setFormError(`Please select the owner for the kit`);
        }

        createKit({
            variables: {
                name: form.name_of_kit,
                ownerId: ownerIdToSend,
                iconId,
                public: form.privacy === "Private" ? false : true, // if private is selected, make it private; else default to public setting
                description: form.description
            }
        });
    };

    return (
        <Form>
            {formError && (
                <GeneralFormError>
                    <BodyText
                        type="p"
                        size="sm"
                        copyColor={colors.functional.lightMode.sangria}
                    >
                        {formError}
                    </BodyText>
                </GeneralFormError>
            )}

            <FormInput
                validation={validateNameOfKit}
                value={form.name_of_kit}
                onChange={value => handleFormChange(value, "name_of_kit")}
                placeholderText="Name of Kit (required)"
                labelText="Name of Kit (required)"
                required
            />

            <FormSelect
                validation={validatePrivacy}
                required
                onChange={value => handleFormChange(value, "privacy")}
                selectName="privacy"
                placeholderText={formFieldLabelPrivacy}
                labelText={formFieldLabelPrivacy}
                options={[{ option: "Public" }, { option: "Private" }]}
            />

            {/* only if user owns teams, show teams to apply ownership of kit:  */}
            {hasOwnerSelectMenu && (
                <FormSelect
                    validation={validateOwnership}
                    required={hasOwnerSelectMenu}
                    onChange={value => handleFormChange(value, "owner")}
                    selectName="owner"
                    placeholderText={formFieldLabelOwner}
                    labelText={formFieldLabelOwner}
                    options={formattedOwnedTeams}
                />
            )}

            <FormTextArea
                value={form.description}
                onChange={value => handleFormChange(value, "description")}
                placeholderText="Description (optional)"
                labelText="Description (optional)"
                height={convertPxToRem({ px: 115 })}
            />

            <Button
                width="100%"
                type="submit"
                onClick={handleFormSubmit}
                isDisabled={isProcessingRequest}
                isProcessing={isProcessingRequest}
            >
                Create Kit
            </Button>

            <BodyText size="md" type="div" alignment="center">
                <CancelButton onClick={handleShowChooseKit}>
                    Back to all kits
                </CancelButton>
            </BodyText>
        </Form>
    );
};

CreateKit.propTypes = {
    iconId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
    userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
    initialForm: PropTypes.object.isRequired,
    handleShowChooseKit: PropTypes.func.isRequired,
    ownedTeams: PropTypes.arrayOf(
        PropTypes.shape({
            option: PropTypes.string,
            slug: PropTypes.string
        })
    ),
    allKits: PropTypes.arrayOf(
        PropTypes.shape({ name: PropTypes.string.isRequired })
    ).isRequired
};

export default CreateKit;
